import { getAPIService, postAPIService } from "./BaseServce";

export interface AutopilotApiKey {
  name: string;
  prefix: string;
  revoked: boolean;
  created: string;
}

export interface AutopilotUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  role: string;
  company: {
    id: number;
    name: string;
    subscription_tier: "free_tier" | "bill_tier";
    renewal_date: string | null;
    frames_used: number;
    estimated_cost: { currency: string; unit_amount_decimal: number } | null;
  } | null;
  api_keys: AutopilotApiKey[];
}

export interface CreateAutopilotUserPayload {
  email: string;
  address: string;
  first_name: string;
  last_name?: string;
  company_name?: string;
  api_key_label?: string;
}

export const getAutopilotUser = async (
  email: string,
): Promise<AutopilotUser | null> => {
  const res = await getAPIService(
    `/autopilot/user?email=${encodeURIComponent(email)}`,
  ).catch((err: { status?: number; data?: { error?: string } }) => {
    if (err?.status === 404) return null;
    throw err;
  });
  return res ? res.data : null;
};

export const createAutopilotUser = async (
  payload: CreateAutopilotUserPayload,
): Promise<{
  company_id: number;
  user_id: number;
  slug: string;
  api_key: string;
}> => {
  const res = await postAPIService("/autopilot/user", payload);
  return res.data;
};

export const generateAutopilotApiKey = async (
  userEmail: string,
  apiKeyLabel?: string,
): Promise<{ api_key: string }> => {
  const res = await postAPIService("/autopilot/user/api-key", {
    user_email: userEmail,
    ...(apiKeyLabel ? { api_key_label: apiKeyLabel } : {}),
  });
  return res.data;
};

export const revokeAutopilotApiKey = async (
  userEmail: string,
  apiKeyPrefix: string,
): Promise<{ message: string }> => {
  const res = await postAPIService("/autopilot/user/api-key/revoke", {
    user_email: userEmail,
    api_key_prefix: apiKeyPrefix,
  });
  return res.data;
};

export const getAutopilotUsage = async () => {
  const res = await getAPIService("/autopilot/usage");
  return res.data;
};

export interface MonthlyUsageEntry {
  month: string;
  frames_processed: number;
  assets_processed: number;
  inferences_executed: number;
}

export interface WeeklyUsageByService {
  service: string;
  frames_processed: number;
  assets_processed: number;
  inferences_executed: number;
}

export interface WeeklyUsageCycle {
  start_date: string;
  end_date: string;
}

export interface AutopilotUserUsage {
  company: { id: number; name: string };
  monthly_usage: MonthlyUsageEntry[];
  weekly_usage_by_service: WeeklyUsageByService[];
  weekly_usage_cycle: WeeklyUsageCycle;
}

export const getAutopilotUserUsage = async (
  userEmail: string,
): Promise<AutopilotUserUsage> => {
  const res = await getAPIService(
    `/autopilot/user/usage?user_email=${encodeURIComponent(userEmail)}`,
  );
  return res.data;
};

export interface UpdateAutopilotSubscriptionResponse {
  user_email?: string;
  subscription_tier?: string;
  requires_payment_method?: boolean;
}

export const updateAutopilotSubscription = async (
  userEmail: string,
  subscriptionTier: "free_tier" | "bill_tier",
): Promise<UpdateAutopilotSubscriptionResponse> => {
  const res = await postAPIService("/autopilot/user/company/subscription", {
    user_email: userEmail,
    subscription_tier: subscriptionTier,
  });
  return res.data;
};

export interface AutopilotBillingStatus {
  has_failed_payment: boolean;
  invoice: { id: string; amount: number; currency: string } | null;
}

export const getAutopilotBillingStatus =
  async (): Promise<AutopilotBillingStatus> => {
    const res = await getAPIService("/autopilot/billing/status");
    return res.data;
  };

export const retryAutopilotPayment = async (
  paymentMethodId: string,
): Promise<{ payment_intent_secret: string; invoice_id: string }> => {
  const res = await postAPIService("/autopilot/billing/retry", {
    payment_method_id: paymentMethodId,
  });
  return res.data;
};

export const getAutopilotExportActivity = async (
  startDate: string,
  endDate?: string,
) => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  const query = params.toString();
  const url = query
    ? `/autopilot/export-activity?${query}`
    : "/autopilot/export-activity";

  const res = await getAPIService(url);
  return res.data;
};
